'use client';
import React from 'react';
import { useModel } from '@/context/ModelContext';
import { fmtNum } from '@/lib/financial-model';
import InputRow from './InputRow';

export default function CapacitySection() {
  const { assumptions, updateAssumption, model } = useModel();

  return (
    <div>
      <InputRow
        label="TPH Capacity"
        unit="Tonnes Per Hour"
        note="Rated throughput of the pellet line — scaled from 2 TPH baseline"
        confidence="High"
        value={assumptions?.tphCapacity}
        onChange={(v) => updateAssumption('tphCapacity', v)}
        min={0.5}
        max={20}
        step={0.5}
      />
      <InputRow
        label="Operating Hours per Day"
        unit="Hours/day"
        note="Continuous operation — 2 shifts typically; 20 hrs allows maintenance window"
        confidence="Medium"
        value={assumptions?.operatingHoursPerDay}
        onChange={(v) => updateAssumption('operatingHoursPerDay', v)}
        min={8}
        max={24}
        step={0.5}
      />
      <InputRow
        label="Operating Days per Year"
        unit="Days/year"
        note="Standard for continuous biomass plants; allows 65 days for maintenance/seasonal"
        confidence="High"
        value={assumptions?.operatingDaysPerYear}
        onChange={(v) => updateAssumption('operatingDaysPerYear', v)}
        min={200}
        max={365}
        step={5}
      />
      <InputRow
        label="Design Annual Capacity"
        unit="Tonnes/year"
        note="Calculated: TPH × Hours/day × Days/year"
        isCalculated
        calculatedValue={`${fmtNum(model?.designCapacity)} T`}
        value={model?.designCapacity}
        onChange={() => {}}
      />
      <InputRow
        label="Year 1 Average Utilisation"
        unit="% of design"
        note="Ramp-up year — conservative 75% accounts for commissioning, market development"
        confidence="Medium"
        value={assumptions?.year1Utilisation}
        onChange={(v) => updateAssumption('year1Utilisation', v)}
        min={10}
        max={100}
        step={1}
        isPercent
      />
      <InputRow
        label="Year 2+ Steady-State Utilisation"
        unit="% of design"
        note="Steady state after ramp — 95% is achievable with secured offtake"
        confidence="Medium"
        value={assumptions?.year2PlusUtilisation}
        onChange={(v) => updateAssumption('year2PlusUtilisation', v)}
        min={10}
        max={100}
        step={1}
        isPercent
      />
      <div className="mt-3 p-3 bg-muted/50 rounded-lg">
        <p className="text-xs text-muted-foreground">
          Year 1 Production:{' '}
          <span className="font-mono font-semibold text-foreground">
            {fmtNum(model?.years?.[1]?.production ?? 0)} T
          </span>
          {' · '}
          Year 2+ Production:{' '}
          <span className="font-mono font-semibold text-foreground">
            {fmtNum(model?.years?.[2]?.production ?? 0)} T
          </span>
        </p>
      </div>
    </div>
  );
}
